import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Chip, IconButton, Tooltip } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import PublishIcon from '@material-ui/icons/Publish';
import styles from './externalAuthList.module.scss';
import {
  ExternalAuthConfig,
  ExternalAuthStatus,
  GrantType,
  GrantTypeNames,
} from './types';

const statusClassName = (status?: ExternalAuthStatus): string => {
  switch (status) {
    case ExternalAuthStatus.Valid:
      return styles.statusValid;
    case ExternalAuthStatus.Invalid:
      return styles.statusInvalid;
    case ExternalAuthStatus.Expired:
      return styles.statusExpired;
    default:
      return styles.statusUnknown;
  }
};

interface UseExternalAuthColumnsParams {
  orgId: string;
  statusById: Record<string, ExternalAuthStatus>;
  externalAuthAction: any;
  commonAction: any;
  onEdit: (item: ExternalAuthConfig) => void;
  onDeleteRequest: (item: ExternalAuthConfig) => void;
}

interface UseExternalAuthColumnsResult {
  columns: object[];
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const useExternalAuthColumns = ({
  orgId,
  statusById,
  externalAuthAction,
  commonAction,
  onEdit,
  onDeleteRequest,
}: UseExternalAuthColumnsParams): UseExternalAuthColumnsResult => {
  const [uploadTargetId, setUploadTargetId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAuthorize = useCallback(
    (item: ExternalAuthConfig) => {
      if (!item.id) return;
      const newTab = window.open('', '_blank');
      externalAuthAction.authorizeExternalAuth({
        orgId,
        id: item.id,
        commonAction,
        onSuccess: (url: string) => {
          if (newTab) {
            newTab.location.href = url;
          } else {
            window.open(url, '_blank');
          }
        },
      });
    },
    [orgId, externalAuthAction, commonAction],
  );

  const handleGetToken = useCallback(
    (item: ExternalAuthConfig) => {
      if (!item.id) return;
      externalAuthAction.getExternalAuthToken({
        orgId,
        id: item.id,
        commonAction,
      });
    },
    [orgId, externalAuthAction, commonAction],
  );

  const handleCheckStatus = useCallback(
    (item: ExternalAuthConfig) => {
      if (!item.id) return;
      externalAuthAction.getExternalAuthStatus({ orgId, id: item.id });
    },
    [orgId, externalAuthAction],
  );

  const handleUploadCertificate = useCallback((item: ExternalAuthConfig) => {
    if (!item.id) return;
    setUploadTargetId(item.id);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !uploadTargetId) return;
      externalAuthAction.uploadCertificate({
        orgId,
        id: uploadTargetId,
        file,
        commonAction,
      });
      e.target.value = '';
      setUploadTargetId(null);
    },
    [orgId, uploadTargetId, externalAuthAction, commonAction],
  );

  const renderTokenCell = useCallback(
    ({ row }: any) => {
      const item: ExternalAuthConfig = row.original;
      const { grantTypeId, status, hasCertificate } = item;

      const showAuthorize =
        grantTypeId === GrantType.AuthorizationCode &&
        (status === ExternalAuthStatus.Unknown ||
          status === ExternalAuthStatus.Invalid);

      const showGetToken =
        grantTypeId === GrantType.ClientCredentials ||
        grantTypeId === GrantType.EmailCredentials ||
        (grantTypeId === GrantType.AuthorizationCode && !showAuthorize) ||
        (grantTypeId === GrantType.JwtCertificate && hasCertificate);

      const showCertificate = grantTypeId === GrantType.JwtCertificate;

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {showAuthorize && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleAuthorize(item)}
            >
              Authorize
            </Button>
          )}
          {showGetToken && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleGetToken(item)}
            >
              Get Token
            </Button>
          )}
          {showCertificate && (
            <>
              <Tooltip title="Upload Certificate">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<PublishIcon />}
                  onClick={() => handleUploadCertificate(item)}
                >
                  Certificate
                </Button>
              </Tooltip>
              {hasCertificate && (
                <Chip
                  size="small"
                  label="Uploaded"
                  className={styles.statusValid}
                />
              )}
            </>
          )}
        </div>
      );
    },
    [handleAuthorize, handleGetToken, handleUploadCertificate],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        sortType: 'caseInsensitive',
      },
      {
        Header: 'Grant Type',
        Cell: ({ row }: any) =>
          GrantTypeNames[row.original.grantTypeId as GrantType] ?? '-',
      },
      {
        Header: 'Status',
        Cell: ({ row }: any) => {
          const item: ExternalAuthConfig = row.original;
          const status = (item.id && statusById[item.id]) || item.status;
          return (
            <Chip
              size="small"
              label={(status ?? 'unknown').toUpperCase()}
              className={statusClassName(status)}
              onClick={() => handleCheckStatus(item)}
            />
          );
        },
      },
      {
        Header: 'Token',
        Cell: renderTokenCell,
      },
      {
        Header: ' ',
        Cell: ({ row }: any) => (
          <div style={{ display: 'flex' }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onEdit(row.original)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onDeleteRequest(row.original)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [statusById, handleCheckStatus, renderTokenCell, onEdit, onDeleteRequest],
  );

  return { columns, fileInputRef, handleFileChange };
};

export default useExternalAuthColumns;
